import { EndPoint } from '@/enums/EndPoint'
import { LocaleDefinition } from '@/enums/LocaleDefinition'
import { MessageType } from '@/enums/MessageType'
import { SettingsTab } from '@/enums/SettingsTab'
import { StatusField } from '@/enums/StatusField'
import { StoreId } from '@/enums/StoreId'
import type { IGlobalSettings } from '@/interfaces/IGlobalSettings'
import type { IItemsPerPageOption } from '@/interfaces/IItemsPerPageOption'
import { useAPIStore } from '@/stores/db/ApiStore'
import dayjs from 'dayjs'
import { cloneDeep, isEqual, merge } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocale } from 'vuetify'
import { useSnackbarStore } from './SnackbarStore'

export const useSettingsStore = defineStore(
  StoreId.Settings,
  () => {
    // STORES, IMPORTS, & COMPOSABLES
    const i18n = useI18n({ useScope: 'global' })
    const apiStore = useAPIStore()
    const snackbarStore = useSnackbarStore()
    const vuetifyLocale = useLocale()

    // REACTIVE VARIABLES
    const activeDatasetId = ref<number | null>(null)
    const localeDefinitions = ref<Array<LocaleDefinition>>([
      LocaleDefinition.German,
      LocaleDefinition.English,
      LocaleDefinition.Spanish,
      LocaleDefinition.French,
      LocaleDefinition.Japanese,
      LocaleDefinition.Korean,
      LocaleDefinition.Portuguese,
      LocaleDefinition.ChineseSimplified,
      LocaleDefinition.ChineseTraditional
    ])

    // Stored in database
    const defaultGlobals: IGlobalSettings = {
      clearance: {
        daysToExpire: 365,
        daysUntilDue: 30,
        enabled: true,
        expires: true
      },
      devices: {
        daysToExpireCalibration: 365,
        daysToExpirePlan: 365,
        daysUntilDueCalibration: 60,
        daysUntilDuePlan: 60
      },
      testing: {
        daysToExpire: 365,
        daysUntilDue: 30,
        expires: true
      },
      training: {
        daysToExpire: 365,
        daysUntilDue: 30,
        enabled: true,
        expires: true
      },
      defaultLocaleCode: LocaleDefinition.English.code
    }

    const disabledHeaders = ref<Record<string, Array<string>>>({})
    const itemsPerPageOptions = ref<Array<IItemsPerPageOption>>([
      { title: '5', value: 5 },
      { title: '10', value: 10 },
      { title: '15', value: 15 },
      { title: '20', value: 20 },
      { title: '25', value: 25 },
      { title: '50', value: 50 },
      { title: '100', value: 100 },
      { title: '500', value: 500 },
      { title: '1000', value: 1000 }
    ])
    const globals = ref<IGlobalSettings>(cloneDeep(defaultGlobals))
    const id = ref<number | undefined>()
    const isLoading = ref<boolean>()
    const localeCode = ref<string>(LocaleDefinition.English.code)
    const oldGlobals = ref<IGlobalSettings>(cloneDeep(defaultGlobals))
    const sapAccountId = ref<string>('')
    const selectedTab = ref<string>(SettingsTab.Account)

    // WATCHERS
    watch(localeCode, () => {
      if (localeCode.value) {
        i18n.locale.value = localeCode.value
        vuetifyLocale.current.value = locale.value.vuetify
        dayjs.locale(locale.value.language)
      }
    })

    watch(
      globals,
      (value) => {
        if (value && !localeCode.value) {
          localeCode.value = globals.value.defaultLocaleCode
        }
      },
      { deep: true }
    )

    // COMPUTED
    const isDirty = computed(() => {
      return !isEqual(globals.value, oldGlobals.value)
    })

    const locale = computed(() => {
      const locale = localeDefinitions.value.find((locale) => {
        return locale.code === localeCode.value
      })

      if (!locale) {
        return LocaleDefinition.English
      }
      return locale
    })

    const languageOptions = computed(() => {
      return localeDefinitions.value.map((locale) => {
        return { title: locale.title, value: locale.code }
      })
    })

    // FUNCTIONS
    const cancel = () => {
      if (oldGlobals.value) {
        globals.value = cloneDeep(oldGlobals.value)
      }
    }

    const _getCategory = (statusField: StatusField) => {
      switch (statusField) {
        case StatusField.ClearanceExpiration: {
          return globals.value.clearance
        }
        case StatusField.TestDueDate: {
          return globals.value.testing
        }
        case StatusField.TrainingExpiration: {
          return globals.value.training
        }
      }
    }

    const getDaysUntilDue = (field: StatusField): number => {
      const category = _getCategory(field)
      if (category) {
        return category.daysUntilDue
      }
      return 30
    }

    const expires = (field: StatusField): boolean => {
      const category = _getCategory(field)
      if (category) {
        return category.expires
      }
      return true
    }

    const load = async (): Promise<boolean> => {
      if (isLoading.value) {
        return false
      }

      isLoading.value = true

      try {
        const response = await apiStore.getMany(EndPoint.AccountSettings)
        if (!response.error && response.data?.count === 0) {
          return await save()
        } else {
          let jsonGlobals
          try {
            id.value = response.data?.results[0]?.id
            sapAccountId.value = response.data?.results[0]?.sapAccountId
            jsonGlobals = JSON.parse(response.data?.results[0].globals)
            globals.value = merge(defaultGlobals, jsonGlobals)
            oldGlobals.value = cloneDeep(globals.value)
            return true
          } catch {
            snackbarStore.addMessage(i18n.t('warnings.loadGlobalSettings'), MessageType.Warning)
            return false
          }
        }
      } catch {
        snackbarStore.addMessage(i18n.t('warnings.loadGlobalSettings'), MessageType.Warning)
        return false
      }
    }

    const save = async () => {
      try {
        const response = await apiStore.save(
          EndPoint.AccountSettings,
          {
            id: id.value,
            globals: JSON.stringify(globals.value)
          },
          false
        )
        if (response.error) {
          return false
        }
        oldGlobals.value = cloneDeep(globals.value)
        snackbarStore.addMessage(i18n.t('info.saveGlobalSettings'), MessageType.Success)
        return true
      } catch {
        snackbarStore.addMessage(i18n.t('warnings.saveGlobalSettings'), MessageType.Warning)
        return false
      }
    }

    return {
      activeDatasetId,
      disabledHeaders,
      globals,
      isDirty,
      isLoading,
      itemsPerPageOptions,
      languageOptions,
      locale,
      localeCode,
      sapAccountId,
      selectedTab,
      cancel,
      expires,
      getDaysUntilDue,
      load,
      save
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: ['activeDatasetId', 'disabledHeaders', 'localeCode', 'selectedTab']
    }
  }
)
